// Subscriptions API — типизированные обёртки над backend-эндпоинтами.
// Контракт сверен с apps/api subscriptions.controller + templates.dto + subscriptions.dto.

import { api, ApiError } from '@/lib/api';
import type { SubscriptionStatus, SubscriptionTemplateType } from '@/lib/contracts';

// Ре-экспорт единых контракт-типов (канон — @/lib/contracts).
export type { SubscriptionStatus, SubscriptionTemplateType };

// --- Полная конфигурация шаблона (3 таба формы) ---
// Контракт сверен с apps/api/.../dto/templates.dto.ts (snake_case транспорт).

/** Режим срока абонемента (динамический абонемент). */
export type SubscriptionValidityMode = 'rolling' | 'calendar_month';

/** Активация: с момента покупки / с первого посещения. */
export type ActivationMode = 'from_purchase' | 'from_first_visit';

/** Тип цены: указанное значение / свободная / минимальная. */
export type PriceType = 'fixed' | 'free' | 'min';

/** Предмет расчёта (фискализация): услуга / товар. */
export type CalculationSubject = 'service' | 'goods';

/**
 * Сетка «действует в выбранный период»: ключ — день недели (0=Пн … 6=Вс),
 * значение — список активных часов (0..23). null = действует всегда.
 */
export type ActivePeriod = Record<string, number[]>;

export interface SubscriptionTemplate {
  id: string;
  name: string;
  description: string | null;
  type: SubscriptionTemplateType;
  price_kopecks: number;
  sessions_count: number | null;
  duration_days: number | null;
  validity_mode: SubscriptionValidityMode;
  price_per_session_kopecks: number | null;
  allow_debt: boolean;
  status: 'active' | 'archived';
  branch_ids: string[];
  group_ids: string[];
  // --- Полная конфигурация ---
  deactivation_at: string | null;
  buyable_in_widgets: boolean;
  buyable_in_app: boolean;
  rental_condition_ids: string[];
  visit_location_ids: string[];
  deduct_days_on_miss: number | null;
  require_active_contract: boolean;
  activation_mode: ActivationMode;
  freeze_days: number | null;
  freeze_min_term: number | null;
  freeze_max_count: number | null;
  freeze_unlimited: boolean;
  active_period: ActivePeriod | null;
  price_type: PriceType;
  recurrent_autorenew: boolean;
  // Юр. лицо — ссылка на словарь; налоговая ставка и система налогообложения
  // берутся из выбранного юр. лица, а не из отдельных полей шаблона.
  legal_entity_id: string | null;
  calculation_subject: CalculationSubject;
  salary_visit_percent: number;
  salary_sale_percent: number;
  created_at: string;
  updated_at: string;
}

/**
 * Тело POST /subscription-templates. Поля транспорта — snake_case, цена — в
 * копейках (форма конвертирует рубли через rublesToKopecks). Опциональные поля
 * имеют дефолты на сервере (см. CreateSubscriptionTemplateSchema).
 */
export interface CreateTemplateBody {
  name: string;
  description?: string;
  type: SubscriptionTemplateType;
  price_kopecks: number;
  sessions_count?: number;
  duration_days?: number;
  validity_mode?: SubscriptionValidityMode;
  price_per_session_kopecks?: number;
  allow_debt?: boolean;
  branch_ids?: string[];
  group_ids?: string[];
  // --- Tab «Общие настройки» ---
  deactivation_at?: string | null;
  buyable_in_widgets?: boolean;
  buyable_in_app?: boolean;
  // --- Tab «Состав номенклатурной позиции» ---
  rental_condition_ids?: string[];
  visit_location_ids?: string[];
  deduct_days_on_miss?: number | null;
  require_active_contract?: boolean;
  activation_mode?: ActivationMode;
  freeze_days?: number | null;
  freeze_min_term?: number | null;
  freeze_max_count?: number | null;
  freeze_unlimited?: boolean;
  active_period?: ActivePeriod | null;
  // --- Tab «Платежные настройки» ---
  price_type?: PriceType;
  recurrent_autorenew?: boolean;
  legal_entity_id?: string | null;
  calculation_subject?: CalculationSubject;
  salary_visit_percent?: number;
  salary_sale_percent?: number;
}

/**
 * Тело PATCH /subscription-templates/:id. Все поля create-body опциональны, плюс
 * управление статусом (активен / архив). Контракт сверен с
 * apps/api/.../dto/templates.dto.ts UpdateSubscriptionTemplateSchema (.strict()).
 */
export type UpdateTemplateBody = Omit<
  Partial<CreateTemplateBody>,
  'description' | 'sessions_count' | 'duration_days' | 'price_per_session_kopecks'
> & {
  status?: 'active' | 'archived';
  // Эти поля на сервере .nullable() (UpdateSubscriptionTemplateSchema) — при
  // очистке поля в форме редактирования шлём null, поэтому переопределяем тип
  // (Omit нужен, т.к. пересечение string & (string|null) сузило бы до string).
  description?: string | null;
  sessions_count?: number | null;
  duration_days?: number | null;
  // При переключении validity_mode обратно на 'rolling' шлём null, чтобы
  // очистить ранее сохранённую цену за занятие (calendar_month).
  price_per_session_kopecks?: number | null;
  /**
   * Осознанное согласие на смену типа, которая сужает круг занятий уже
   * проданных абонементов. Без него сервер отвечает 409
   * `subscription_template.type_change_affects_sold`.
   */
  type_change_confirmed?: boolean;
};

export interface SubscriptionListItem {
  id: string;
  client_id: string;
  /** «Фамилия Имя» владельца-клиента. null, если клиент не найден. */
  client_name: string | null;
  template_id: string;
  /** Название абонемента (шаблона). null, если шаблон не найден. */
  template_name: string | null;
  /** Тип шаблона (single/personal/…) — для фильтра «абонемент × тип занятия». */
  template_type: SubscriptionTemplateType | null;
  branch_id: string;
  status: SubscriptionStatus;
  price_at_sale_kopecks: number;
  discount_amount_kopecks: number;
  sold_at: string;
  activated_at: string;
  expires_at: string | null;
  sessions_total: number | null;
  sessions_remaining: number | null;
  /**
   * «Фамилия Имя» продавца — берётся с продажи (sales.sold_by). null у импорта
   * и legacy: там продажи в CRM нет, значит и продавца тоже.
   */
  sold_by_name: string | null;
  /**
   * Продажа, которую можно доплатить до абонемента (план 120 Б), либо null.
   * Сервер заполняет её, ТОЛЬКО когда доплата возможна прямо сейчас: продажа
   * завершена, товар разовый или пробный, смена продажи ещё открыта. Поэтому
   * кнопку можно показывать по одному признаку, не гадая.
   */
  upgradable_sale_id?: string | null;
  /**
   * Возвраты по продаже абонемента, ждущие подтверждения (план 168 §2.4) —
   * плашка «Возврат N ₽ ждёт подтверждения» в карточке клиента. Без
   * CRM-продажи 0. Необязательное: старый сервер поля в списке не отдаёт.
   */
  pending_refund_kopecks?: number;
}

export interface ListSubscriptionsParams {
  clientId?: string;
  status?: SubscriptionStatus;
  branchId?: string;
  /** Поиск по ФИО / телефону клиента (ILIKE на сервере). */
  search?: string;
  /** Фильтр по типу шаблона (номенклатурной позиции). */
  templateType?: SubscriptionTemplateType;
  /** Фильтр по конкретному абонементу (шаблону). */
  templateId?: string;
  limit?: number;
}

// --- Детали абонемента (окно как в MobiFitness) ---
// Контракт сверен с apps/api subscriptions.controller GET /subscriptions/:id
// (расширенный SubscriptionDetailDto). Транспорт — snake_case, деньги — копейки.
// Многие поля nullable: фронт должен корректно показывать «—»/«Всегда».

/** Тип занятия в истории использования (из groups.type). */
export type LessonType = 'regular' | 'personal' | 'wedding' | 'masterclass';

/** Статус посещения в истории использования. */
export type UsageStatus = 'scheduled' | 'attended' | 'no_show' | 'cancelled' | 'no_show_charged';

/**
 * Конфиг шаблона, проброшенный внутрь деталей абонемента
 * (разделы Пакет / Активация / Заморозка / Период / Изменения при продаже).
 */
export interface SubscriptionDetailTemplate {
  name: string;
  type: string;
  price_kopecks: number;
  /** Момент последней смены цены (ISO) или null — для пометки на проданных. */
  price_changed_at: string | null;
  activation_mode: ActivationMode;
  freeze_days: number | null;
  freeze_min_term: number | null;
  freeze_max_count: number | null;
  freeze_unlimited: boolean;
  /** {"0".."6": [часы]} 0=Пн..6=Вс; null = действует всегда. */
  active_period: ActivePeriod | null;
}

/** Локация, где действует абонемент (право посещения). */
export interface SubscriptionVisitLocation {
  branch_id: string;
  name: string;
}

/** Bucket «состава» абонемента (направление + остаток занятий). */
export interface SubscriptionEntitlement {
  id: string;
  group_id: string | null;
  group_name: string | null; // null = универсальный bucket («Все направления»)
  sessions_total: number | null;
  sessions_remaining: number | null;
  priority: number;
}

/** Агрегат заморозок по абонементу (раздел «Заморозка»). */
export interface SubscriptionFreezeSummary {
  total_days: number | null;
  used_days: number;
  available_days: number | null;
  total_count: number | null;
  used_count: number;
  available_count: number | null;
  unlimited: boolean;
}

/** Активная (или одобренная) заморозка. */
export interface SubscriptionActiveFreeze {
  id: string;
  from_date: string;
  to_date: string;
  days: number;
  status: 'approved' | 'active';
}

/** Строка истории использования (списание занятия). */
export interface SubscriptionUsageEntry {
  attendance_id: string;
  occurrence_id: string; // class_occurrences.id — для клика по посещению → окно занятия
  lesson_date: string; // ISO — class_occurrences.scheduled_at
  written_off_at: string; // ISO — attendance.marked_at
  lesson_name: string | null;
  branch_name: string | null;
  lesson_type: LessonType | string | null;
  teacher_name: string | null;
  status: UsageStatus;
}

/**
 * GET /subscriptions/:id — детали одного абонемента.
 * Расширяет SubscriptionListItem конфигом шаблона, локациями, составом,
 * агрегатом заморозок и историей использования.
 */
export interface SubscriptionFreezeHistoryItem {
  id: string;
  /** Когда операцию провели. */
  requested_at: string;
  from_date: string;
  to_date: string;
  days: number;
  status: string;
  reason: string | null;
  /** Кто заморозил. null — системная заморозка или сотрудник удалён. */
  employee_name: string | null;
}

export interface SubscriptionDetailDto extends SubscriptionListItem {
  /** Короткий номер (план 116) — ссылка /subscriptions/1234. */
  no: string | null;
  /** Фактически уплачено (net продажи) — Ф1 плана 39. */
  net_paid_kopecks: number;
  /**
   * Получено денег по продаже — оплаты кроме долга плюс погашения (план 166).
   * База формы возврата и её верхняя граница. У импорта = `net_paid_kopecks`.
   */
  paid_kopecks: number;
  /** Остаток долга по продаже, как на экране «Долги» (план 166). У импорта 0. */
  outstanding_debt_kopecks: number;
  /** Уже возвращено, включая ожидающие одобрения (план 166). У импорта 0. */
  refunded_kopecks: number;
  /** Из них ещё не исполнено — ждут одобрения (план 166). У импорта 0. */
  pending_refund_kopecks: number;
  /** Продажа, которой создан абонемент (для возврата). null у legacy/импорта. */
  sale_id: string | null;
  /** Телефон клиента (блок «Абонемент»). */
  client_phone: string | null;
  /** Способ(ы) оплаты продажи (блок «Данные»), активные. */
  payment_methods: string[];
  /** Активные аллокации оплаты (id/метод/сумма) — для смены способа (П5). */
  payment_allocations: Array<{ id: string; method: string; amount_kopecks: number }>;
  template: SubscriptionDetailTemplate;
  visit_locations: SubscriptionVisitLocation[];
  /**
   * Группы, в которых действует абонемент. Пустой список — «любая незакрытая
   * группа»: так работают все абонементы, проданные до плана 101.
   */
  covered_groups: Array<{ group_id: string; name: string }>;
  entitlements: SubscriptionEntitlement[];
  freeze_summary: SubscriptionFreezeSummary;
  active_freeze: SubscriptionActiveFreeze | null;
  /**
   * История заморозок (правка владельца 29.08): кто, когда и на какой срок.
   * Счётчиков «дни/заморозки» не хватало — по ним нельзя ни проверить
   * конкретную операцию, ни спросить с того, кто её сделал.
   */
  freeze_history: SubscriptionFreezeHistoryItem[];
  usage_history: SubscriptionUsageEntry[];
}

export function listTemplates(includeArchived = false): Promise<{ data: SubscriptionTemplate[] }> {
  const qs = includeArchived ? '?include_archived=true' : '';
  return api.get<{ data: SubscriptionTemplate[] }>(`/subscription-templates${qs}`);
}

export function createTemplate(body: CreateTemplateBody): Promise<SubscriptionTemplate> {
  return api.post<SubscriptionTemplate>('/subscription-templates', body);
}

export function updateTemplate(
  id: string,
  body: UpdateTemplateBody,
): Promise<SubscriptionTemplate> {
  return api.patch<SubscriptionTemplate>(`/subscription-templates/${id}`, body);
}

/** Жёсткое удаление товара/услуги. 409, если есть проданные абонементы. */
export function deleteTemplate(id: string): Promise<void> {
  return api.delete<void>(`/subscription-templates/${id}`);
}

function buildSubscriptionsQuery(params: ListSubscriptionsParams): string {
  const sp = new URLSearchParams();
  if (params.clientId) sp.set('client_id', params.clientId);
  if (params.status) sp.set('status', params.status);
  if (params.branchId) sp.set('branch_id', params.branchId);
  if (params.search) sp.set('search', params.search);
  if (params.templateType) sp.set('template_type', params.templateType);
  if (params.templateId) sp.set('template_id', params.templateId);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function listSubscriptions(
  params: ListSubscriptionsParams = {},
): Promise<{ data: SubscriptionListItem[] }> {
  return api.get<{ data: SubscriptionListItem[] }>(
    `/subscriptions${buildSubscriptionsQuery(params)}`,
  );
}

/** Детали одного абонемента (раздел «Детали абонемента»). */
export function getSubscription(id: string): Promise<SubscriptionDetailDto> {
  return api.get<SubscriptionDetailDto>(`/subscriptions/${id}`);
}

/** Дашборд над «Проданными»: активных / сумма / средний чек / к продлению. */
export interface SubscriptionsStats {
  active_count: number;
  /** Выручка по активным абонементам ЗА ВЫЧЕТОМ скидки (решение владельца 03.09.2026). */
  active_sum_kopeck: number;
  /**
   * Сумма скидок в тех же активных абонементах. Сервер отдаёт её рядом с
   * выручкой под будущий отчёт по скидкам; плитки под неё пока нет.
   */
  active_discount_kopeck: number;
  avg_ticket_kopeck: number;
  renewals_this_month: number;
}

export function getSubscriptionsStats(branchId?: string): Promise<SubscriptionsStats> {
  const qs = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : '';
  return api.get<SubscriptionsStats>(`/subscriptions/stats${qs}`);
}

/**
 * Возврат импортного абонемента (без CRM-продажи) — ручной кассовый расход.
 * POST /subscriptions/:id/refund-legacy. Для абонементов с sale_id — кассовый refund.
 */
/** Data-driven вариант фильтра «Абонемент» в «Проданных». */
export interface SubscriptionTemplateOption {
  template_id: string;
  template_name: string;
  template_status: 'active' | 'archived' | string;
}

/**
 * GET /subscriptions/template-options — только шаблоны, у которых есть проданные
 * абонементы под текущие фильтры (статус/филиал/поиск).
 */
export function listSubscriptionTemplateOptions(params: {
  status?: SubscriptionStatus | undefined;
  branchId?: string | undefined;
  search?: string | undefined;
}): Promise<SubscriptionTemplateOption[]> {
  const sp = new URLSearchParams();
  if (params.status) sp.set('status', params.status);
  if (params.branchId) sp.set('branch_id', params.branchId);
  if (params.search) sp.set('search', params.search);
  const qs = sp.toString();
  return api
    .get<{
      items: SubscriptionTemplateOption[];
    }>(`/subscriptions/template-options${qs ? `?${qs}` : ''}`)
    .then((r) => r.items);
}

export function refundLegacySubscription(
  id: string,
  body: {
    amount_kopecks: number;
    method: 'cash' | 'terminal' | 'qr' | 'card_transfer';
    reason: string;
  },
): Promise<{ status: 'executed' }> {
  return api.post<{ status: 'executed' }>(`/subscriptions/${id}/refund-legacy`, body);
}

/** Продлить абонемент на N дней — POST /subscriptions/:id/extend. */
export function extendSubscription(id: string, days: number): Promise<void> {
  return api.post<void>(`/subscriptions/${id}/extend`, { days });
}

/** Заморозить абонемент на N дней — POST /subscriptions/:id/freeze. */
export function freezeSubscription(id: string, days: number): Promise<void> {
  return api.post<void>(`/subscriptions/${id}/freeze`, { days });
}

/** Разморозить абонемент — POST /subscriptions/:id/unfreeze. */
export function unfreezeSubscription(id: string): Promise<void> {
  return api.post<void>(`/subscriptions/${id}/unfreeze`, {});
}

/**
 * Досрочно закрыть абонемент: он станет истёкшим (план 82). Не возврат и не
 * удаление — деньги не трогаем, вернуть в действующие можно «Продлить».
 */
export function closeSubscription(id: string, reason?: string): Promise<void> {
  return api.post<void>(`/subscriptions/${id}/close`, reason ? { reason } : {});
}

/**
 * «Активировать сейчас» (план 148): ожидающий активации абонемент начинает
 * действовать сразу, даты не меняются. Право `subscription:activate_early`.
 */
export function activateSubscriptionEarly(id: string): Promise<void> {
  return api.post<void>(`/subscriptions/${id}/activate`, {});
}

/** Ручная корректировка остатка занятий — PATCH /subscriptions/:id/remaining. */
export function adjustSubscriptionRemaining(id: string, sessionsRemaining: number): Promise<void> {
  return api.patch<void>(`/subscriptions/${id}/remaining`, {
    sessions_remaining: sessionsRemaining,
  });
}

/**
 * Заменить группы, которые покрывает абонемент (план 101 §2.4).
 * Replace-семантика: отправляем полный список; пустой = «любая группа».
 */
export function setSubscriptionGroups(id: string, groupIds: string[]): Promise<void> {
  return api.put<void>(`/subscriptions/${id}/groups`, { group_ids: groupIds });
}

/**
 * Правка дат проданного абонемента (план 101 §2.7).
 * Для календарного смена даты начала пересчитывает срок на конец нового месяца.
 *
 * План 158 Ф2: списанные посещения, выпавшие из нового срока, сервер без
 * согласия не переносит — отказ 409 `subscription.dates_orphan_visits` с
 * перечнем (`orphanVisitsOf`); `move_visits: true` — согласие на перенос.
 */
export function setSubscriptionDates(
  id: string,
  dates: { activated_at?: string; expires_at?: string | null; move_visits?: boolean },
): Promise<void> {
  return api.put<void>(`/subscriptions/${id}/dates`, dates);
}

/** Списанное посещение вне нового срока — строка отказа правки дат (план 158 Ф2). */
export interface OrphanVisitDto {
  attendance_id: string;
  /** Дата занятия по времени школы, YYYY-MM-DD. */
  date: string;
  group_name: string;
  /** Номер абонемента, на который переедет списание; null — переносить некуда. */
  move_to_subscription_no: string | null;
}

/** Перечень посещений из отказа `subscription.dates_orphan_visits`, иначе null. */
export function orphanVisitsOf(e: unknown): OrphanVisitDto[] | null {
  if (!(e instanceof ApiError) || e.code !== 'subscription.dates_orphan_visits') return null;
  const visits = e.problem.errors?.['visits'];
  return Array.isArray(visits) ? (visits as OrphanVisitDto[]) : null;
}
